import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { BadgePercent, ChevronRight, Copy, Check, MapPin, ExternalLink, Tag } from "lucide-react";
import { PRO_CATEGORY_ORDER, catMeta, catLabel } from "../data/proCategories";
import { useGuestPromos } from "./useGuestPromos";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const RED = "#FF3B30";
const BLUE = "#007AFF";

const T = {
  it: { title: "Promo locali", subtitle: "Offerte esclusive per il tuo soggiorno", empty: "Nessuno sconto disponibile al momento.", activate: "Attivala ora", copy: "Copia codice", copied: "Copiato!", whereUse: "Dove usarlo", maps: "Apri su Google Maps", code: "Il tuo codice sconto" },
  en: { title: "Local deals", subtitle: "Exclusive deals for your stay", empty: "No discounts available right now.", activate: "Activate now", copy: "Copy code", copied: "Copied!", whereUse: "Where to use it", maps: "Open in Google Maps", code: "Your discount code" },
  fr: { title: "Offres locales", subtitle: "Offres exclusives pour votre séjour", empty: "Aucune réduction disponible pour le moment.", activate: "Activer maintenant", copy: "Copier le code", copied: "Copié !", whereUse: "Où l'utiliser", maps: "Ouvrir dans Google Maps", code: "Votre code de réduction" },
  es: { title: "Ofertas locales", subtitle: "Ofertas exclusivas para tu estancia", empty: "No hay descuentos disponibles ahora.", activate: "Actívalo ahora", copy: "Copiar código", copied: "¡Copiado!", whereUse: "Dónde usarlo", maps: "Abrir en Google Maps", code: "Tu código de descuento" },
  de: { title: "Lokale Angebote", subtitle: "Exklusive Angebote für deinen Aufenthalt", empty: "Zurzeit keine Rabatte verfügbar.", activate: "Jetzt aktivieren", copy: "Code kopieren", copied: "Kopiert!", whereUse: "Wo einlösen", maps: "In Google Maps öffnen", code: "Dein Rabattcode" },
};

export const promoTexts = (lang) => T[lang] || T.it;

const CategoryHeader = ({ category, lang }) => {
  const meta = catMeta(category);
  const Icon = meta.icon;
  return (
    <div className="flex items-center gap-2 pt-1" data-testid={`promo-cat-${category}`}>
      <span className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0" style={{ background: `${meta.color}1a`, color: meta.color }}>
        <Icon className="w-4 h-4" strokeWidth={2.2} />
      </span>
      <p className="text-[13px] font-black uppercase tracking-wide text-zinc-700">{catLabel(category, lang)}</p>
    </div>
  );
};

const fileUrl = (v) => {
  if (!v) return "";
  if (v.startsWith("http") || v.startsWith("/api/")) return v;
  if (v.startsWith("Hostland/")) return `${API}/files/${v}`;
  return `${API}/files/${v}`;
};

export const PromoCard = ({ promo, tt, index, propertyId }) => {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const accent = RED;

  const track = () => {
    if (promo.isPro) { axios.post(`${API}/pro/promos/${promo.promo_id}/click`).catch(() => {}); return; }
    const q = propertyId ? `?property_id=${encodeURIComponent(propertyId)}` : "";
    axios.post(`${API}/promos/click/${promo.promo_id}${q}`).catch(() => {});
  };

  const onActivate = () => {
    track();
    if (promo.promo_type === "link" && promo.url) {
      window.open(promo.url, "_blank", "noopener,noreferrer");
    } else {
      setOpen((o) => !o);
    }
  };

  const copyCode = () => {
    navigator.clipboard?.writeText(promo.code || "");
    setCopied(true);
    setTimeout(() => setCopied(false), 1600);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05, duration: 0.4 }}
      className="relative bg-white rounded-3xl border border-zinc-100 shadow-sm overflow-hidden"
      data-testid={`promo-card-${promo.promo_id}`}
    >
      {promo.pill && (
        <div
          className="absolute top-3 right-3 z-10 px-3 py-1.5 rounded-full text-white text-xs font-black shadow-lg"
          style={{ background: accent }}
          data-testid="promo-pill"
        >
          {promo.pill}
        </div>
      )}
      <div className="p-4 flex gap-4">
        <div className="shrink-0 w-16 h-16 rounded-2xl bg-zinc-100 overflow-hidden flex items-center justify-center">
          {promo.service_icon ? (
            <img src={fileUrl(promo.service_icon)} alt={promo.service_name} className="w-full h-full object-cover" />
          ) : (
            <Tag className="w-7 h-7 text-zinc-300" />
          )}
        </div>
        <div className="min-w-0 flex-1 pr-14">
          <h3 className="font-black text-zinc-900 leading-tight truncate">{promo.service_name}</h3>
          {promo.service_desc && <p className="text-xs text-zinc-500 mt-0.5 leading-snug line-clamp-2">{promo.service_desc}</p>}
          {promo.promo_desc && <p className="text-sm font-bold mt-1.5 leading-snug" style={{ color: accent }}>{promo.promo_desc}</p>}
        </div>
      </div>

      <div className="px-4 pb-4 flex flex-col gap-2">
        <button
          onClick={onActivate}
          className="w-full py-3 rounded-2xl text-white font-black text-sm inline-flex items-center justify-center gap-2 transition-transform active:scale-95"
          style={{ background: accent }}
          data-testid="promo-activate-btn"
        >
          {tt.activate} <ChevronRight className="w-4 h-4" />
        </button>

        {open && promo.promo_type === "code" && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} className="overflow-hidden">
            <div className="mt-1 rounded-2xl bg-zinc-50 border border-dashed border-zinc-300 p-3">
              <p className="text-[10px] uppercase tracking-wider font-bold text-zinc-400 mb-1">{tt.code}</p>
              <button onClick={copyCode} className="w-full flex items-center justify-between gap-2 bg-white rounded-xl border border-zinc-200 px-3 py-2.5" data-testid="promo-code-copy">
                <span className="font-mono font-black text-zinc-900 tracking-wide truncate">{promo.code}</span>
                {copied ? <Check className="w-4 h-4 text-emerald-500 shrink-0" /> : <Copy className="w-4 h-4 text-zinc-400 shrink-0" />}
              </button>
              {copied && <p className="text-[11px] text-emerald-600 font-bold mt-1.5 text-center">{tt.copied}</p>}
              {promo.code_usage_url && (
                <a href={promo.code_usage_url} target="_blank" rel="noopener noreferrer" className="mt-2 flex items-center justify-center gap-1.5 text-xs font-bold text-zinc-600 hover:text-zinc-900" data-testid="promo-where-use">
                  <ExternalLink className="w-3.5 h-3.5" /> {tt.whereUse}
                </a>
              )}
            </div>
          </motion.div>
        )}

        {promo.maps_url && (
          <a href={promo.maps_url} target="_blank" rel="noopener noreferrer" className="w-full py-2 rounded-2xl bg-zinc-100 text-zinc-700 font-bold text-xs inline-flex items-center justify-center gap-1.5 hover:bg-zinc-200 transition-colors" data-testid="promo-maps-btn">
            <MapPin className="w-3.5 h-3.5" /> {tt.maps}
          </a>
        )}
      </div>
    </motion.div>
  );
};

const GuestPromos = ({ propertyId, lang = "it", onBack, hideHeader = false }) => {
  const { promos, loading } = useGuestPromos(propertyId, lang);
  const tt = T[lang] || T.it;

  return (
    <div className="gb-section py-6" data-testid="guest-promos-view">
      {!hideHeader && (
      <div className="flex items-center gap-3 mb-5">
        <button onClick={onBack} className="p-2.5 bg-white rounded-2xl shadow-sm border border-zinc-100" data-testid="promos-back-btn">
          <ChevronRight className="w-5 h-5 text-zinc-400 rotate-180" />
        </button>
        <div>
          <h2 className="text-xl font-black text-zinc-900 flex items-center gap-2">
            <BadgePercent className="w-5 h-5" style={{ color: RED }} /> {tt.title}
          </h2>
          <p className="text-xs text-zinc-500">{tt.subtitle}</p>
        </div>
      </div>
      )}

      {loading ? (
        <div className="space-y-3">{[0, 1].map((i) => <div key={i} className="h-32 rounded-3xl bg-zinc-100 animate-pulse" />)}</div>
      ) : promos.length === 0 ? (
        <div className="text-center py-16 text-zinc-400">
          <BadgePercent className="w-12 h-12 mx-auto mb-3 opacity-40" />
          <p className="font-medium text-sm">{tt.empty}</p>
        </div>
      ) : (
        <>
          <div className="space-y-3">
          {(() => {
            const nodes = [];
            let idx = 0;
            const effCat = (p) => (p.source === "hostland" ? "__PARTNER__" : (p.category || "Altro"));
            // Categorie normali (Pro + promo locali dell'host) in ordine
            PRO_CATEGORY_ORDER.forEach((cat) => {
              const items = promos.filter((p) => effCat(p) === cat);
              if (!items.length) return;
              nodes.push(
                <div key={`h-${cat}`} className={idx ? "pt-2" : ""}>
                  <CategoryHeader category={cat} lang={lang} />
                </div>
              );
              items.forEach((p) => nodes.push(<PromoCard key={p.promo_id} promo={p} tt={tt} index={idx++} propertyId={propertyId} />));
            });
            // Partner Hostland (VDH) sempre in fondo, etichetta rossa
            const partner = promos.filter((p) => effCat(p) === "__PARTNER__");
            if (partner.length) {
              const plabel = { it: "Partner", en: "Partners", fr: "Partenaires", es: "Socios", de: "Partner" }[lang] || "Partner";
              nodes.push(
                <div key="h-partner" className={idx ? "pt-2" : ""} data-testid="promo-cat-partner">
                  <div className="flex items-center gap-2 pt-1">
                    <span className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0" style={{ background: "rgba(255,59,48,0.10)", color: RED }}>
                      <BadgePercent className="w-4 h-4" strokeWidth={2.2} />
                    </span>
                    <p className="text-[13px] font-black uppercase tracking-wide" style={{ color: RED }}>{plabel}</p>
                  </div>
                </div>
              );
              partner.forEach((p) => nodes.push(<PromoCard key={p.promo_id} promo={p} tt={tt} index={idx++} propertyId={propertyId} />));
            }
            return nodes;
          })()}
          </div>
        </>
      )}
    </div>
  );
};

export default GuestPromos;
